/*!
 * vue-dermis v0.10.1 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { withDefaultProps } from '@aspectus/vue-utils';
import { createBlockGenerator } from '@aspectus/bem';
import { partial } from 'ramda';
import { generator } from '@aspectus/vue-bem-styled-tag';
import { mapSame } from '@aspectus/vue-bem-styling';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var b = createBlockGenerator({
  n: 'ds-'
});
var t = createBlockGenerator({
  n: 't-'
});
var u = createBlockGenerator({
  n: 'u-'
});
var ud = createBlockGenerator({
  n: 'u-',
  v: '-'
});

var tag = function tag(name) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var bg = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : b;
  var block = bg(name);
  return [_objectSpread2({
    name: name
  }, generator(_objectSpread2({
    tag: 'tag',
    block: block
  }, options))), block];
};
var el = function el(name, bl) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var blockName = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : name;
  return _objectSpread2({
    name: name
  }, generator(_objectSpread2({
    tag: 'tag',
    block: partial(bl, [blockName])
  }, options)));
};
var inner = function inner(name) {
  return function (h, context, _ref) {
    var block = _ref.block;
    return [h('div', {
      class: block(name)
    }, context.children)];
  };
};
var body = inner('body');

var SIZE = mapSame('size');
var SPACE = mapSame('space');
var IN_OF = mapSame('indent', 'offset');

var dtag = function dtag(tg) {
  return withDefaultProps({
    tag: tg
  }, 'tag');
};

var _tag = tag('caption', {
  modifiers: SIZE
}),
    _tag2 = _slicedToArray(_tag, 1),
    Caption = _tag2[0];

var _tag3 = tag('inliner', {
  modifiers: SIZE,
  children: body
}),
    _tag4 = _slicedToArray(_tag3, 1),
    Inliner = _tag4[0];

var _tag5 = tag('link'),
    _tag6 = _slicedToArray(_tag5, 1),
    Link = _tag6[0];

var _tag7 = tag('aspect-ratio', {
  modifiers: mapSame('ratio')
}),
    _tag8 = _slicedToArray(_tag7, 2),
    AspectRatio = _tag8[0],
    aspectRatioBlock = _tag8[1];
var AspectRatioBody = el('aspect-ratio-body', aspectRatioBlock, 'body');

var _tag9 = tag('panel', {
  modifiers: SPACE
}),
    _tag10 = _slicedToArray(_tag9, 2),
    Panel = _tag10[0],
    panelBlock = _tag10[1];
var PanelElement = el('panel-element', panelBlock, {
  modifiers: IN_OF
}, 'element');

var _tag11 = tag('section', {
  modifiers: SPACE
}),
    _tag12 = _slicedToArray(_tag11, 2),
    Section = _tag12[0],
    sectionBlock = _tag12[1];
var SectionElement = el('section-element', sectionBlock, {
  modifiers: IN_OF
}, 'element');

var _tag13 = tag('table'),
    _tag14 = _slicedToArray(_tag13, 2),
    Table = _tag14[0],
    tableBlock = _tag14[1];
var TableCaption = el('table-caption', tableBlock, {
  tag: dtag('caption')
}, 'caption');
var TableRow = el('table-row', tableBlock, {
  tag: dtag('tr'),
  modifiers: mapSame('relief')
}, 'row');
var TableCell = el('table-cell', tableBlock, {
  tag: dtag('td'),
  modifiers: mapSame('head')
}, 'cell'); // Typography

var _tag15 = tag('content', {}, t),
    _tag16 = _slicedToArray(_tag15, 1),
    Content = _tag16[0];

var _tag17 = tag('words', {
  modifiers: mapSame('fill')
}, t),
    _tag18 = _slicedToArray(_tag17, 1),
    Words = _tag18[0]; // Utils

var _tag19 = tag('display', {
  modifiers: mapSame('hidden', 'block')
}, ud),
    _tag20 = _slicedToArray(_tag19, 2),
    Display = _tag20[0],
    displayBlock = _tag20[1];

var _tag21 = tag('spreaded-interactor', {}, u),
    _tag22 = _slicedToArray(_tag21, 2),
    SpreadedInteractor = _tag22[0],
    spreadedInteractorBlock = _tag22[1];

var children = function children(h, context, _ref) {
  var block = _ref.block;

  if (context.children) {
    return context.scopedSlots.default(_objectSpread2({
      block: block
    }, context.props));
  }

  var result = [];
  var _context$props = context.props,
      src = _context$props.src,
      label = _context$props.label;

  if (src) {
    result.push(h('img', {
      class: block('image'),
      attrs: {
        src: src,
        alt: label
      }
    }));
  } else if (label && label.charAt) {
    result.push(h('div', {
      class: block('label')
    }, [label.charAt(0)]));
  }

  return result;
};

var _tag$1 = tag('avatar', {
  modifiers: SIZE,
  props: {
    src: String,
    label: String
  },
  children: children
}),
    _tag2$1 = _slicedToArray(_tag$1, 1),
    Avatar = _tag2$1[0];

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$prefix = _ref.prefix,
      prefix = _ref$prefix === void 0 ? 'ds-' : _ref$prefix,
      _ref$prefixType = _ref.prefixType,
      prefixType = _ref$prefixType === void 0 ? 't-' : _ref$prefixType,
      _ref$prefixUtils = _ref.prefixUtils,
      prefixUtils = _ref$prefixUtils === void 0 ? 'u-' : _ref$prefixUtils;

  Vue.component(prefix + Caption.name, Caption);
  Vue.component(prefix + Inliner.name, Inliner);
  Vue.component(prefix + Link.name, Link);
  Vue.component(prefix + Avatar.name, Avatar);
  Vue.component(prefix + AspectRatio.name, AspectRatio);
  Vue.component(prefix + AspectRatioBody.name, AspectRatioBody);
  Vue.component(prefix + Panel.name, Panel);
  Vue.component(prefix + PanelElement.name, PanelElement);
  Vue.component(prefix + Section.name, Section);
  Vue.component(prefix + SectionElement.name, SectionElement);
  Vue.component(prefix + Table.name, Table);
  Vue.component(prefix + TableCaption.name, TableCaption);
  Vue.component(prefix + TableRow.name, TableRow);
  Vue.component(prefix + TableCell.name, TableCell);
  Vue.component(prefixType + Content.name, Content);
  Vue.component(prefixType + Words.name, Words);
  Vue.component(prefixUtils + Display.name, Display);
  Vue.component(prefixUtils + SpreadedInteractor.name, SpreadedInteractor);
}
var main = {
  install: install
};

export default main;
export { AspectRatio, AspectRatioBody, Avatar, Caption, Content, Display, Inliner, Link, Panel, PanelElement, Section, SectionElement, SpreadedInteractor, Table, TableCaption, TableCell, TableRow, Words, aspectRatioBlock, displayBlock, el, inner, install, panelBlock, sectionBlock, spreadedInteractorBlock, tableBlock, tag };
